'use client'

import { toast } from '@/core/lib/toast'

import { offerReplyCandidate } from '@/features/veluvit/components/chat/sheets/reply-candidate-modal'
import { ADULT_REQUIRED } from '@/features/veluvit/services/adult.service'
import { applyNextSettings } from '@/features/veluvit/services/preset.actions'
import { parseDirective } from '@/features/veluvit/lib/engine-commands'
import { isPlayerSegment } from '@/features/veluvit/lib/chat'
import {
  readPendingRequest,
  type PendingRequest,
} from '@/features/veluvit/lib/chat-request'
import { resolveChatMode } from '@/features/veluvit/lib/chat-mode'
import { go } from '@/features/veluvit/lib/navigation'
import { readRef } from '@/features/veluvit/lib/referral'
import { personaText as serializePersona } from '@/features/veluvit/lib/persona'
import { turnCost, workFor } from '@/features/veluvit/lib/rules'
import { roomSchema } from '@/features/veluvit/schemas/api.schema'
import {
  requestId,
  api,
  ApiError,
  OperationError,
  getOperation,
  runOperation,
  waitOperation,
  type Operation,
} from '@/features/veluvit/services/api'
import {
  ensureAccount,
  refreshSession,
} from '@/features/veluvit/services/session.service'
import { openPaywall } from '@/features/veluvit/services/wallet.actions'
import {
  readLocal,
  useChatStore,
  writeLocal,
} from '@/features/veluvit/stores/chat.store'
import {
  roomFor,
  useSessionStore,
} from '@/features/veluvit/stores/session.store'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

import type {
  Room,
  RoomResult,
  Segment,
} from '@/features/veluvit/types/api.types'
import type { Catalog } from '@/features/veluvit/types/catalog.types'

/* 대화방 동작 — 레퍼런스 v2-live.js (ensureRoom · transmit · runCmd · accept …)
   생성 요청(턴·이어서 말하기·다시 쓰기·명령어·기록·추천·스냅·장면)은 백엔드에서 비동기 작업이라 runOperation 이 끝날 때까지 기다린다. */

type TurnOptions = { reasoning?: 'fast' | 'deep' }

const userId = () => useSessionStore.getState().data?.user?.id
const creating = new Map<string, Promise<{ room: Room; fresh: boolean }>>()
const creatingNew = new Map<
  string,
  { payload: string; promise: Promise<Room> }
>()
const loadingRoom = new Map<string, Promise<Room>>()
const attached = new Map<string, AbortController>()
const cancelling = new Set<string>()

function active(
  workId: string,
  roomId: string,
  identity: number,
  controller: AbortController
) {
  return (
    !controller.signal.aborted &&
    useSessionStore.getState().identity === identity &&
    roomFor(workId)?.id === roomId
  )
}

export function detachRoomOperation(workId: string) {
  attached.get(workId)?.abort()
  attached.delete(workId)
  useChatStore.getState().patch(workId, {
    busy: false,
    loading: null,
    pendingOperation: null,
    lastOperation: null,
    tentativeSegments: [],
    tentativePhase: null,
    tentativeCursor: null,
  })
}

function attach(workId: string) {
  attached.get(workId)?.abort()
  const controller = new AbortController()
  attached.set(workId, controller)
  return controller
}

function unresolved(roomId: string) {
  const pending = readPendingRequest(
    readLocal<unknown>(userId(), `pending:${roomId}`, null),
    roomId
  )
  return !!pending && pending.status !== 'failed'
}

function requestContext(room: Room) {
  return {
    model: room.model,
    preset: room.preset,
    presetVersion: room.presetVersion,
  }
}

function contextChanged(request: PendingRequest, room: Room) {
  return (
    request.model !== room.model ||
    request.preset !== room.preset ||
    request.presetVersion !== room.presetVersion
  )
}

function requestCost(
  catalog: Catalog,
  room: Room,
  request: PendingRequest
): number | null {
  const data = useSessionStore.getState().data
  if (request.path === 'snap') {
    const cost = room.snapUsage?.cost
    return Number.isSafeInteger(cost) && cost! >= 0 ? cost! : null
  }
  if (request.path === 'suggestion') {
    const cost = data?.config.prices?.suggestions
    return Number.isSafeInteger(cost) && cost! >= 0 ? cost! : null
  }
  return turnCost(catalog, data, room, request.body.mode || 'daily')
}

function isPaidExtra(request: PendingRequest) {
  return request.path === 'snap' || request.path === 'suggestion'
}

function serverPending(
  value: unknown
): { id: string; requestId?: string } | null {
  if (typeof value === 'string') return value ? { id: value } : null
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  if (typeof record.id !== 'string' || !record.id) return null
  return {
    id: record.id,
    ...(typeof record.requestId === 'string'
      ? { requestId: record.requestId }
      : {}),
  }
}

function cleanupRestricted(workId: string, roomId: string) {
  const session = useSessionStore.getState()
  const knownRooms = [
    ...(session.data?.rooms || []),
    ...Object.values(session.rooms),
  ]
  const restricted = new Map(
    knownRooms
      .filter(
        (room) =>
          room.preset === 'adult' ||
          room.requiresAdult ||
          room.adultHistory ||
          room.id === roomId
      )
      .map((room) => [room.id, room])
  )
  const selectedRoom = roomFor(workId)
  if (selectedRoom) restricted.set(selectedRoom.id, selectedRoom)
  for (const room of restricted.values()) {
    writeLocal(userId(), `pending:${room.id}`, null)
    writeLocal(userId(), `draft:${room.id}`, null)
    writeLocal(userId(), `draft:ooc:${room.id}`, null)
    detachRoomOperation(room.workId)
    useChatStore.getState().reset(room.workId)
  }
  session.revokeRestrictedRooms(roomId)
}

function hasRequestMessage(room: Room, id: string) {
  return (
    room.messages.some((message) => message.requestId === id) ||
    !!room.oocMessages?.some((message) => message.requestId === id)
  )
}

function streamUpdate(
  workId: string,
  event: { event: string; id?: string; data: Record<string, unknown> }
) {
  const chat = useChatStore.getState().get(workId)
  const data = event.data
  const channel = data.channel
  if (event.id)
    useChatStore.getState().patch(workId, { tentativeCursor: event.id })
  if (event.event === 'reset') {
    useChatStore.getState().patch(workId, {
      tentativeSegments: [],
      tentativePhase: typeof data.reason === 'string' ? data.reason : 'running',
    })
  } else if (event.event === 'phase' && typeof data.phase === 'string') {
    useChatStore.getState().patch(workId, { tentativePhase: data.phase })
  } else if (
    event.event === 'segment' &&
    channel !== 'analysis' &&
    typeof data.index === 'number' &&
    data.index >= 0 &&
    data.index < 1000 &&
    data.segment &&
    typeof data.segment === 'object' &&
    'type' in data.segment &&
    'text' in data.segment
  ) {
    const segments = chat.tentativeSegments.slice()
    segments[data.index] = data.segment as Segment
    useChatStore
      .getState()
      .patch(workId, { tentativeSegments: segments, tentativePhase: 'running' })
  } else if (
    event.event === 'replace' &&
    channel !== 'analysis' &&
    Array.isArray(data.segments)
  ) {
    useChatStore
      .getState()
      .patch(workId, { tentativeSegments: data.segments as Segment[] })
  } else if (event.event === 'discard') {
    useChatStore
      .getState()
      .patch(workId, { tentativeSegments: [], tentativePhase: null })
  }
}

function operationUpdate(
  workId: string,
  roomId: string,
  identity: number,
  controller: AbortController,
  request: PendingRequest,
  op: Operation
) {
  if (!active(workId, roomId, identity, controller)) return
  const snapshot: PendingRequest = {
    ...request,
    operationId: op.id,
    status:
      op.status === 'queued' || op.status === 'running'
        ? op.status
        : request.status,
  }
  writeLocal(userId(), `pending:${roomId}`, snapshot)
  useChatStore.getState().patch(workId, {
    requestSnapshot: snapshot,
    pendingOperation: op,
    ...(op.status === 'queued' || op.status === 'running'
      ? {}
      : { lastOperation: op }),
  })
}

/** 작품의 대화 페르소나 문장 — 대화 시작 전에 고른 값, 없으면 계정 기본 페르소나 */
/** 방을 만들 때 인트로의 {user} 에 들어갈 이름 — 고른 페르소나, 없으면 기본 페르소나 */
function personaName(workId: string) {
  const chosen = useUiStore.getState().persona[workId]?.name?.trim()
  return (
    chosen ||
    useSessionStore
      .getState()
      .data?.personas?.find((item) => item.isDefault)
      ?.name?.trim() ||
    undefined
  )
}

function personaText(workId: string) {
  const p = useUiStore.getState().persona[workId]
  return p
    ? serializePersona(p)
    : useSessionStore.getState().data?.user?.persona || ''
}

/** 같은 계정의 같은 방을 동시에 여는 동안만 전체 기록을 공유한다. 완료 뒤 재입장 시 권한은 다시 확인한다. */
function fullRoom(identity: number, roomId: string): Promise<Room> {
  const key = `${identity}:${roomId}`
  const running = loadingRoom.get(key)
  if (running) return running
  const task = api<Room>(`/room/${encodeURIComponent(roomId)}/`)
  loadingRoom.set(key, task)
  void task
    .finally(() => {
      if (loadingRoom.get(key) === task) loadingRoom.delete(key)
    })
    .catch(() => null)
  return task
}

/** 방을 연다 — 이어서 볼 방이 있으면 전체 메시지를 받고, 없으면 고른 오프닝으로 새로 만든다 */
export async function ensureRoom(
  catalog: Catalog,
  workId: string
): Promise<{ room: Room; fresh: boolean }> {
  await ensureAccount()
  const session = useSessionStore.getState()
  const identity = session.identity
  if (creatingNew.has(`${identity}:${workId}`))
    throw new Error('새 대화를 만들고 있어요. 잠시 후 다시 시도해 주세요.')
  const selectedId = session.selected[workId]
  const freshIntent = !!session.fresh[workId]
  const existing = session.fresh[workId] ? undefined : roomFor(workId)
  if (existing) {
    const view = useChatStore.getState().get(workId)
    if (view.roomId === existing.id && view.fresh && !view.opened)
      return { room: existing, fresh: true }
    const full = await fullRoom(identity, existing.id)
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== existing.id
    )
      throw new Error('대화방이 바뀌었어요.')
    useSessionStore.getState().select(workId, existing.id)
    useSessionStore.getState().applyRoom(full)
    return { room: full, fresh: false }
  }
  if (selectedId && !freshIntent) {
    const full = await fullRoom(identity, selectedId)
    if (full.workId !== workId)
      throw new Error('선택한 대화방이 이 작품에 속하지 않아요.')
    if (
      identity !== useSessionStore.getState().identity ||
      useSessionStore.getState().selected[workId] !== selectedId
    )
      throw new Error('대화방이 바뀌었어요.')
    useSessionStore.getState().applyRoom(full)
    return { room: full, fresh: false }
  }
  const creatingKey = `${identity}:${workId}`
  const running = creating.get(creatingKey)
  if (running) return running
  const task = (async () => {
    const work = workFor(catalog, workId)
    if (!work) throw new Error('공개되지 않은 작품입니다.')
    const intro = useUiStore.getState().selIntro[workId] || 0
    const opening = work.openings?.[intro] || work.openings?.[0]
    if (!opening?.id) throw new Error('시작 장면을 찾지 못했어요.')
    // ref: 작가 추천 링크로 들어왔으면 작가코드(30일) — 서버가 작가 정산 우대에 쓴다
    const room = await api<Room>('/room/', {
      workId,
      openingId: opening.id,
      persona: personaText(workId),
      personaName: personaName(workId),
      ref: readRef(workId),
    })
    if (
      identity !== useSessionStore.getState().identity ||
      useSessionStore.getState().selected[workId] !== selectedId ||
      !!useSessionStore.getState().fresh[workId] !== freshIntent
    )
      throw new Error('대화방이 바뀌었어요.')
    useSessionStore.getState().markFresh(workId, false)
    useSessionStore.getState().select(workId, room.id)
    await refreshSession()
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      throw new Error('대화방이 바뀌었어요.')
    useSessionStore.getState().applyRoom(room)
    return { room, fresh: true }
  })()
  creating.set(creatingKey, task)
  try {
    return await task
  } finally {
    if (creating.get(creatingKey) === task) creating.delete(creatingKey)
  }
}

/** 고른 시작 장면으로 새 방을 만들고 목록에 넣는다. 이동은 호출 화면이 맡는다. */
export async function createNewRoom(
  catalog: Catalog,
  workId: string,
  options: { openingId: string; persona?: string; sourceRoomId?: string }
): Promise<Room> {
  await ensureAccount()
  const session = useSessionStore.getState()
  const identity = session.identity
  const key = `${identity}:${workId}`
  const work = workFor(catalog, workId)
  if (!work) throw new Error('공개되지 않은 작품입니다.')
  if (
    !options.openingId ||
    !work.openings?.some((opening) => opening.id === options.openingId)
  )
    throw new Error('시작 장면을 찾지 못했어요.')
  const persona = options.persona ?? personaText(workId)
  const ref = readRef(workId)
  const payload = JSON.stringify([
    options.openingId,
    persona,
    ref,
    options.sourceRoomId,
  ])
  const running = creatingNew.get(key)
  if (running) {
    if (running.payload !== payload)
      throw new Error(
        '다른 새 대화를 만들고 있어요. 완료 후 다시 시도해 주세요.'
      )
    return running.promise
  }
  if (creating.has(key))
    throw new Error('대화방을 만들고 있어요. 완료 후 다시 시도해 주세요.')
  const sourceRoomId = roomFor(workId)?.id ?? null
  if (
    options.sourceRoomId !== undefined &&
    options.sourceRoomId !== sourceRoomId
  )
    throw new Error('대화방이 바뀌었어요.')
  const selectedId = session.selected[workId]
  const promise = (async () => {
    const value = await api<unknown>('/room/', {
      workId,
      openingId: options.openingId,
      persona,
      personaName: personaName(workId),
      ref,
    })
    const parsed = roomSchema.safeParse(value)
    if (
      !parsed.success ||
      !parsed.data.id ||
      parsed.data.workId !== workId ||
      parsed.data.id === sourceRoomId ||
      typeof parsed.data.updatedAt !== 'string'
    )
      throw new ApiError(
        '새 방 생성 결과를 확인하지 못했어요. 내 대화에서 결과를 확인해 주세요.',
        0,
        'ROOM_CREATE_UNCONFIRMED'
      )
    const room = parsed.data as Room
    const current = useSessionStore.getState()
    if (current.identity !== identity) throw new Error('계정이 바뀌었어요.')
    const sameSource =
      current.selected[workId] === selectedId &&
      (roomFor(workId)?.id ?? null) === sourceRoomId
    current.applyRoom(room)
    if (!sameSource)
      throw new Error(
        '새 방은 만들었지만 현재 대화방이 바뀌었어요. 내 대화에서 확인해 주세요.'
      )
    current.markFresh(workId, false)
    return room
  })()
  creatingNew.set(key, { payload, promise })
  try {
    return await promise
  } finally {
    if (creatingNew.get(key)?.promise === promise) creatingNew.delete(key)
  }
}

/** 생성 응답으로 확정한 새 방만 연다. 이전 방의 서버 작업과 기록은 취소·삭제하지 않는다. */
export function startCreatedRoom(workId: string, room: Room) {
  const state = useSessionStore.getState()
  if (room.workId !== workId || state.rooms[room.id] !== room)
    throw new Error('새 대화방 정보를 확인하지 못했어요.')
  detachRoomOperation(workId)
  useChatStore.getState().reset(workId)
  useChatStore.getState().patch(workId, {
    roomId: room.id,
    fresh: true,
    opened: false,
    openIdx: 0,
    local: [],
    sugsUsed: false,
  })
  go(`/chat/${room.id}`)
}

/** 서버 응답과 확정된 컬렉션 해금을 반영한다. 관계 수치로 별도 단계 연출을 만들지 않는다. */
export function accept(result: RoomResult) {
  const session = useSessionStore.getState()
  const ownedCount = session.data?.ownedCards?.length ?? 0
  session.applyResult(result)
  const added =
    (useSessionStore.getState().data?.ownedCards?.length ?? 0) - ownedCount
  if (added > 0) toast.show(`🖼 컬렉션 장면 ${added}개를 해금했어요`)
  const room = result.room
  if (!room) return
  useChatStore.getState().patch(room.workId, { roomId: room.id })
}

function errorLine(
  workId: string,
  message: string,
  channel: 'dialogue' | 'ooc' = 'dialogue'
) {
  const chat = useChatStore.getState().get(workId)
  useChatStore.getState().patch(workId, {
    local: [
      ...chat.local.filter((x) => x.t !== 'error'),
      { t: 'error', key: `error-${Date.now()}`, v: message, channel },
    ],
  })
}

/** 요청 ID와 본문은 제출 시 고정한다. 작업 ID를 받으면 이후 복구는 조회만 한다. */
async function transmit(
  workId: string,
  request: PendingRequest,
  needed: number | null,
  resume = false
) {
  const room = roomFor(workId)
  if (!room || room.id !== request.roomId) return
  const identity = useSessionStore.getState().identity
  const controller = attach(workId)
  writeLocal(userId(), `pending:${room.id}`, request)
  const paidExtra = isPaidExtra(request)
  useChatStore.getState().patch(workId, {
    busy: true,
    requestSnapshot: request,
    requestError: null,
    pendingOperation: null,
    ...(paidExtra
      ? {
          loading: {
            kind:
              request.path === 'snap'
                ? ('snaps' as const)
                : ('suggestions' as const),
            since: Date.now(),
          },
        }
      : {
          lastOperation: null,
          tentativeSegments: [],
          tentativePhase: 'queued',
          tentativeCursor: null,
        }),
  })
  try {
    if (
      !resume &&
      useChatStore.getState().get(workId).nextSettings &&
      (await applyNextSettings(workId))
    ) {
      const latest = roomFor(workId)
      if (!active(workId, room.id, identity, controller) || !latest) return
      request = { ...request, ...requestContext(latest) }
      writeLocal(userId(), `pending:${room.id}`, request)
      useChatStore.getState().patch(workId, { requestSnapshot: request })
    }
    const options = {
      signal: controller.signal,
      onOperation: (op: Operation) =>
        operationUpdate(workId, room.id, identity, controller, request, op),
      onStreamEvent: (event: {
        event: string
        id?: string
        data: Record<string, unknown>
      }) => {
        if (
          event.event === 'discard' &&
          event.data.status === 'access_revoked' &&
          active(workId, room.id, identity, controller)
        ) {
          cleanupRestricted(workId, room.id)
          void refreshSession().catch(() => null)
          return
        }
        const channel = event.data.channel
        if (
          active(workId, room.id, identity, controller) &&
          ((channel !== 'ooc' && channel !== 'dialogue') ||
            channel === (request.body.inputType || 'dialogue'))
        )
          streamUpdate(workId, event)
      },
    }
    const result =
      resume && request.operationId
        ? await waitOperation<RoomResult>(request.operationId, options)
        : await runOperation<RoomResult>(
            `/room/${room.id}/${request.path}/`,
            request.body,
            options
          )
    if (!active(workId, room.id, identity, controller)) return
    const finalRoom =
      result.room || (await api<Room>(`/room/${room.id}/`).catch(() => null))
    if (!active(workId, room.id, identity, controller)) return
    if (!finalRoom)
      throw new ApiError(
        paidExtra
          ? '요청은 끝났지만 결과를 확인하지 못했어요. 기존 요청 상태를 다시 확인해 주세요.'
          : '답장은 완료됐지만 방 기록을 아직 확인하지 못했어요. 다시 확인해 주세요.',
        502,
        'ROOM_RESULT_UNAVAILABLE'
      )
    if (finalRoom.id !== room.id || finalRoom.workId !== workId) {
      throw new ApiError(
        '다른 대화의 결과가 도착해 현재 요청을 확정하지 않았어요. 기존 요청 상태를 다시 확인해 주세요.',
        502,
        'ROOM_RESULT_MISMATCH'
      )
    }
    if (request.path === 'snap' && (!result.snap?.id || !result.snap.image)) {
      throw new ApiError(
        '스냅 작업은 끝났지만 결과를 확인하지 못했어요. 기존 요청 상태를 다시 확인해 주세요.',
        502,
        'INVALID_SNAP_RESULT'
      )
    }
    if (
      request.path === 'suggestion' &&
      !Array.isArray(finalRoom.suggestions)
    ) {
      throw new ApiError(
        '추천 작업은 끝났지만 결과를 확인하지 못했어요. 기존 요청 상태를 다시 확인해 주세요.',
        502,
        'INVALID_SUGGESTION_RESULT'
      )
    }
    let continuationError: {
      message: string
      retryable: boolean
      code: string
    } | null = null
    if (request.path === 'continue' && !result.candidate) {
      const previous = new Set(room.messages.map((message) => message.id))
      const replies = finalRoom.messages.filter(
        (message) =>
          message.role === 'assistant' &&
          !message.command &&
          (message.requestId === request.body.requestId ||
            !previous.has(message.id))
      )
      if (!replies.some((message) => message.segments?.some(isPlayerSegment))) {
        continuationError = {
          message: replies.length
            ? '상대 답변은 도착했지만 자동으로 쓴 내 대사가 빠져 있어요.'
            : '새 대화 묶음을 확인하지 못했어요. 대화 기록을 확인해 주세요.',
          retryable: false,
          code: 'CONTINUATION_PLAYER_MISSING',
        }
      } else if (
        !replies.some((message) =>
          message.segments?.some(
            (segment) =>
              segment.type === 'speech' &&
              !isPlayerSegment(segment) &&
              segment.speakerType !== 'narrator' &&
              segment.speakerType !== 'system'
          )
        )
      ) {
        continuationError = {
          message: '내 대사는 도착했지만 상대 답장을 확인하지 못했어요.',
          retryable: false,
          code: 'CONTINUATION_REPLY_MISSING',
        }
      }
    }
    accept({ ...result, room: finalRoom })
    writeLocal(userId(), `pending:${room.id}`, null)
    useChatStore.getState().patch(
      workId,
      paidExtra
        ? {
            sugsUsed: false,
            requestSnapshot: null,
            requestError: null,
            pendingOperation: null,
            loading: null,
          }
        : {
            local: [],
            sugsUsed: false,
            fresh: false,
            requestSnapshot: null,
            requestError: continuationError,
            pendingOperation: null,
            tentativeSegments: [],
            tentativePhase: null,
            tentativeCursor: null,
          }
    )
    if (request.path === 'snap')
      toast.show(
        result.replayed
          ? '📷 이 장면의 스냅은 이미 있어요'
          : '📷 스냅을 남겼어요',
        { duration: 2500 }
      )
    if (request.path === 'suggestion')
      toast.show('↻ 새 답장 3개로 바꿨어요', { duration: 2500 })
    if (result.candidate && !offerReplyCandidate(workId, result.candidate))
      toast.show(
        '새 답변 후보를 표시하지 못했어요. 방을 새로고침해 후보 기록을 확인해 주세요.'
      )
    if (result.snapError) toast.show(result.snapError, { duration: 4000 })
    if (result.room?.ending)
      toast.show(`📖 ${result.room.ending.title}`, { duration: 3000 })
  } catch (error) {
    if (!active(workId, room.id, identity, controller)) return
    if (
      error instanceof ApiError &&
      error.status === 403 &&
      (error.code === ADULT_REQUIRED ||
        error.failure?.reason === 'access_revoked')
    ) {
      cleanupRestricted(workId, room.id)
      await refreshSession().catch(() => null)
      return
    }
    const funds =
      error instanceof ApiError && error.code === 'INSUFFICIENT_FUNDS'
    const message = funds
      ? '젬이 부족해요. 충전 후 다시 보내 주세요.'
      : error instanceof Error
        ? error.message
        : '연결을 확인해 주세요.'
    const preparationFailed =
      error instanceof ApiError &&
      (error.code === 'CSRF_PREPARATION_FAILED' ||
        error.code === 'INVALID_CSRF_RESPONSE')
    const unknown =
      !preparationFailed &&
      !(error instanceof OperationError) &&
      (!(error instanceof ApiError) ||
        error.unknownOutcome ||
        error.status === 0 ||
        error.status >= 500 ||
        error.code === 'OPERATION_TIMEOUT' ||
        error.code === 'ROOM_RESULT_UNAVAILABLE')
    const snapshot =
      useChatStore.getState().get(workId).requestSnapshot || request
    let rejectionCode: PendingRequest['rejectionCode']
    if (
      !unknown &&
      !snapshot.operationId &&
      error instanceof ApiError &&
      !(error instanceof OperationError)
    ) {
      if (
        error.code === 'INSUFFICIENT_FUNDS' ||
        error.code === 'CSRF_PREPARATION_FAILED' ||
        error.code === 'INVALID_CSRF_RESPONSE'
      )
        rejectionCode = error.code
    }
    const retryable =
      !!rejectionCode ||
      (error instanceof OperationError &&
        error.operation.failure?.retryable === true)
    const failed: PendingRequest = {
      ...snapshot,
      status: unknown ? 'unknown' : 'failed',
      retryable,
      rejectionCode,
    }
    writeLocal(userId(), `pending:${room.id}`, failed)
    const latest = await api<Room>(`/room/${room.id}/`).catch(() => null)
    if (!active(workId, room.id, identity, controller)) return
    if (latest) useSessionStore.getState().applyRoom(latest)
    useChatStore.getState().patch(workId, {
      ...(paidExtra
        ? { loading: null }
        : { local: [], tentativeSegments: [], tentativePhase: null }),
      requestSnapshot: failed,
      requestError: {
        message,
        retryable,
        ...(error instanceof ApiError ? { code: error.code } : {}),
      },
      pendingOperation: null,
      ...(error instanceof OperationError
        ? { lastOperation: error.operation }
        : {}),
    })
    if (!paidExtra)
      errorLine(workId, message, request.body.inputType || 'dialogue')
    const draftKey =
      request.body.inputType === 'ooc'
        ? `draft:ooc:${room.id}`
        : `draft:${room.id}`
    if (
      request.body.text &&
      !readLocal<string | null>(userId(), draftKey, null)
    ) {
      writeLocal(userId(), draftKey, request.body.text)
      useChatStore.getState().patch(workId, {
        restoreDraft: request.body.text,
        restoreDraftChannel: request.body.inputType || 'dialogue',
      })
    }
    // 잔액 부족은 결제 안내까지 띄운다 — 입력한 대화는 그대로 남는다
    if (
      error instanceof ApiError &&
      error.code === 'INSUFFICIENT_FUNDS' &&
      needed !== null
    )
      openPaywall(needed)
    if (error instanceof ApiError && error.code?.startsWith('REASONING_'))
      void refreshSession().catch(() => null)
  } finally {
    if (attached.get(workId) === controller) {
      attached.delete(workId)
      if (active(workId, room.id, identity, controller))
        useChatStore.getState().patch(workId, { busy: false })
    }
  }
}

async function resumeUntracked(
  workId: string,
  roomId: string,
  operationId: string,
  identity: number
) {
  const controller = attach(workId)
  useChatStore
    .getState()
    .patch(workId, { busy: true, tentativePhase: 'queued' })
  try {
    const result = await waitOperation<RoomResult>(operationId, {
      signal: controller.signal,
      onOperation: (op) => {
        if (active(workId, roomId, identity, controller))
          useChatStore.getState().patch(workId, {
            pendingOperation: op,
            ...(op.status === 'queued' || op.status === 'running'
              ? {}
              : { lastOperation: op }),
          })
      },
      onStreamEvent: (event) => {
        if (active(workId, roomId, identity, controller))
          streamUpdate(workId, event)
      },
    })
    if (active(workId, roomId, identity, controller)) {
      const finalRoom =
        result.room || (await api<Room>(`/room/${roomId}/`).catch(() => null))
      if (!active(workId, roomId, identity, controller)) return
      if (!finalRoom)
        throw new ApiError(
          '완료된 방 기록을 확인하지 못했어요.',
          502,
          'ROOM_RESULT_UNAVAILABLE'
        )
      accept({ ...result, room: finalRoom })
      useChatStore.getState().patch(workId, {
        pendingOperation: null,
        tentativeSegments: [],
        tentativePhase: null,
        busy: false,
      })
    }
  } catch (error) {
    if (active(workId, roomId, identity, controller)) {
      errorLine(
        workId,
        error instanceof Error ? error.message : '연결을 확인해 주세요.'
      )
      useChatStore.getState().patch(workId, {
        pendingOperation: null,
        tentativeSegments: [],
        tentativePhase: null,
        busy: false,
        ...(error instanceof OperationError
          ? { lastOperation: error.operation }
          : {}),
      })
    }
  } finally {
    if (attached.get(workId) === controller) attached.delete(workId)
  }
}

/** 대사 보내기 (v2 sendMsg) — 보낸 말은 응답 전까지 낙관적으로 보여 준다 */
export function sendTurn(
  catalog: Catalog,
  workId: string,
  text: string,
  options: TurnOptions = {}
): boolean {
  const chat = useChatStore.getState().get(workId)
  if (!text.trim() || chat.busy || !chat.opened) return false
  if (text.length > 3000) {
    toast.show('메시지는 3,000자까지 보낼 수 있어요')
    return false
  }
  if (text.trimStart().startsWith('!')) {
    toast.show('사용할 수 있는 명령어를 ! 메뉴에서 선택해 주세요.')
    return false
  }
  // 엔진 연출(/점프 · /사건 …) — 글 전체는 기록용으로 그대로 보내고, 지시는 directive 로 따로 실어 서버가 프롬프트에 넣는다
  const directive = text.trimStart().startsWith('/')
    ? parseDirective(text.trimStart())
    : null
  if (text.trimStart().startsWith('/') && !directive) {
    toast.show('없는 명령어예요. / 를 눌러 목록에서 골라 주세요.')
    return false
  }
  if (directive?.arg && [...directive.arg].length > 1000) {
    toast.show('장면 연출에 덧붙일 말은 1,000자까지 입력해 주세요.')
    return false
  }
  const room = roomFor(workId)
  if (!room) return false
  if (unresolved(room.id)) return false
  const mode = resolveChatMode(useUiStore.getState().modes[workId], room)
  const cost = turnCost(catalog, useSessionStore.getState().data, room, mode)
  if (cost === null) {
    toast.show('요금을 확인할 수 없어요. 잠시 후 다시 시도해 주세요.')
    return false
  }
  const id = requestId()
  const request: PendingRequest = {
    roomId: room.id,
    path: 'turn',
    body: {
      requestId: id,
      text,
      mode,
      ...(directive ? { directive } : {}),
      ...options,
    },
    operationId: null,
    status: 'submitting',
    ...requestContext(room),
  }
  useChatStore.getState().patch(workId, {
    local: [{ t: 'user', key: `pending-${id}`, v: text, channel: 'dialogue' }],
    sugsUsed: true,
  })
  void transmit(workId, request, cost)
  return true
}

export function continueTurn(
  catalog: Catalog,
  workId: string,
  reasoning?: 'fast' | 'deep'
): boolean {
  const chat = useChatStore.getState().get(workId)
  const room = roomFor(workId)
  if (!room || chat.busy || !chat.opened) return false
  if (unresolved(room.id)) return false
  const mode = resolveChatMode(useUiStore.getState().modes[workId], room)
  const cost = turnCost(catalog, useSessionStore.getState().data, room, mode)
  if (cost === null) {
    toast.show('요금을 확인할 수 없어요. 잠시 후 다시 시도해 주세요.')
    return false
  }
  const request: PendingRequest = {
    roomId: room.id,
    path: 'continue',
    body: { requestId: requestId(), mode, ...(reasoning ? { reasoning } : {}) },
    operationId: null,
    status: 'submitting',
    ...requestContext(room),
  }
  const identity = useSessionStore.getState().identity
  const controller = attach(workId)
  const current = () => active(workId, room.id, identity, controller)
  useChatStore.getState().patch(workId, {
    busy: true,
    sugsUsed: true,
    local: [],
    requestSnapshot: request,
    requestError: null,
    lastOperation: null,
    pendingOperation: null,
    tentativeSegments: [],
    tentativePhase: 'preparing',
  })
  void (async () => {
    try {
      if (room.settings?.allowPlayerDialogue === false) {
        const latest = await api<Room>(
          `/room/${room.id}/`,
          { settings: { ...room.settings, allowPlayerDialogue: true } },
          'PATCH',
          controller.signal
        )
        if (!current()) return
        if (
          latest.id !== room.id ||
          latest.settings?.allowPlayerDialogue === false
        )
          throw new Error(
            '내 대사 자동 생성을 켜지 못했어요. 잠시 후 다시 시도해 주세요.'
          )
        if (contextChanged(request, latest))
          throw new Error(
            '대화 설정이 바뀌었어요. 설정을 확인한 뒤 다시 눌러 주세요.'
          )
        useSessionStore.getState().applyRoom(latest)
      }
      if (current()) await transmit(workId, request, cost)
    } catch (error) {
      if (current())
        useChatStore.getState().patch(workId, {
          requestSnapshot: null,
          requestError: {
            message:
              error instanceof Error
                ? error.message
                : '자동 이어쓰기를 시작하지 못했어요.',
            retryable: false,
          },
          tentativePhase: null,
        })
    } finally {
      if (attached.get(workId) === controller) {
        attached.delete(workId)
        if (current()) useChatStore.getState().patch(workId, { busy: false })
      }
    }
  })()
  return true
}

/** 마지막 답장 다시 쓰기 — 서버가 새 버전을 만들어 지금 버전으로 둔다. 이전 버전은 버전 넘기기로 다시 볼 수 있다 */
export async function regenerateReply(
  workId: string,
  messageId: string,
  cost: number
) {
  const room = roomFor(workId)
  const chat = useChatStore.getState().get(workId)
  if (!room || chat.busy || unresolved(room.id)) return
  const request: PendingRequest = {
    roomId: room.id,
    path: 'regenerate',
    body: { requestId: requestId(), messageId },
    operationId: null,
    status: 'submitting',
    ...requestContext(room),
  }
  useChatStore.getState().patch(workId, { busy: true, sugsUsed: true })
  await transmit(workId, request, cost)
}

/** 답장 버전 고르기 — 고른 버전이 대화 기록과 다음 턴의 문맥이 된다 */
export async function selectVariant(
  workId: string,
  messageId: string,
  index: number
) {
  const room = roomFor(workId)
  if (!room || useChatStore.getState().get(workId).busy) return
  const identity = useSessionStore.getState().identity
  try {
    const latest = await api<Room>(
      `/room/${room.id}/message/${messageId}/variant/`,
      { index }
    )
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    useSessionStore.getState().applyRoom(latest)
  } catch (error) {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      toast.show(
        error instanceof Error ? error.message : '연결을 확인해 주세요.',
        { duration: 3200 }
      )
  }
}

/** 캐릭터 답장 고치기 — 지문·대사 문장만 바꾼다(순서·말하는 사람·장면 컷은 그대로). 고친 문장이 다음 턴의 문맥이 된다 */
export async function editReply(
  workId: string,
  messageId: string,
  segments: Segment[]
) {
  const room = roomFor(workId)
  if (!room) return false
  const identity = useSessionStore.getState().identity
  try {
    const latest = await api<Room>(
      `/room/${room.id}/message/${messageId}/`,
      { segments },
      'PATCH'
    )
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return false
    useSessionStore.getState().applyRoom(latest)
    toast.show('답장을 고쳤어요', { duration: 1600 })
    return true
  } catch (error) {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      toast.show(
        error instanceof Error ? error.message : '연결을 확인해 주세요.',
        { duration: 3200 }
      )
    return false
  }
}

/**
 * 장면 바로 들어가기 — 작품의 자유 진입 장면(work.branches)으로 지금 방에서 곧장 옮긴다. 이전 진행과 관계없고 무료다.
 * 방이 없으면 먼저 만든다. 그 장면에 묶인 카드는 서버가 진입과 함께 지급한다.
 */
export async function enterBranch(
  catalog: Catalog,
  workId: string,
  branchId: string
) {
  const identity = useSessionStore.getState().identity
  try {
    const { room } = await ensureRoom(catalog, workId)
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    const result = await runOperation<RoomResult>(`/room/${room.id}/branch/`, {
      requestId: requestId(),
      branchId,
    })
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    accept(result)
    useChatStore
      .getState()
      .patch(workId, { opened: true, local: [], sugsUsed: false })
    // 대화방 주소는 방 id(chat-route) — 장면에 들어간 방으로
    go(`/chat/${roomFor(workId)?.id || room.id}`)
  } catch (error) {
    if (identity === useSessionStore.getState().identity)
      toast.show(
        error instanceof Error ? error.message : '연결을 확인해 주세요.',
        { duration: 3200 }
      )
  }
}

/** 마지막 캐릭터 답장 지우기 — 내 말은 남아서 이어서 말하기·다시 쓰기로 다시 받을 수 있다. 쓴 재화는 돌려주지 않는다 */
export async function deleteReply(workId: string, messageId: string) {
  const room = roomFor(workId)
  if (!room || useChatStore.getState().get(workId).busy) return
  const identity = useSessionStore.getState().identity
  try {
    const latest = await api<Room>(
      `/room/${room.id}/message/${messageId}/`,
      undefined,
      'DELETE'
    )
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    useSessionStore.getState().applyRoom(latest)
    toast.show('답장을 지웠어요', { duration: 1600 })
  } catch (error) {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      toast.show(
        error instanceof Error ? error.message : '연결을 확인해 주세요.',
        { duration: 3200 }
      )
  }
}

/** 대화 지침 저장 — 다음 답장부터 캐릭터가 참고한다 */
export async function saveGuide(roomId: string, guide: string) {
  const latest = await api<Room>(`/room/${roomId}/`, { guide }, 'PATCH')
  useSessionStore.getState().applyRoom(latest)
  return latest
}

/**
 * 대화 분기 — 이 답장(턴)까지의 대화를 새 방으로 복제한다. 원래 방은 그대로 남고, 새 방으로 옮겨 이어 간다.
 * 새 방은 목록(bootstrap.rooms)에 있어야 roomFor 가 찾으므로 세션을 한 번 새로 받는다. 새 방의 턴 id 는 원래 방과 다르다.
 */
export async function branchRoom(
  workId: string,
  turnId: string,
  title: string
) {
  const room = roomFor(workId)
  if (!room) throw new Error('대화방을 먼저 열어 주세요.')
  const identity = useSessionStore.getState().identity
  const { room: created } = await api<{ roomId: string; room: Room }>(
    `/room/${room.id}/branch/`,
    { requestId: requestId(), turnId, position: 'after', title }
  )
  if (
    identity !== useSessionStore.getState().identity ||
    roomFor(workId)?.id !== room.id
  )
    throw new Error('대화방이 바뀌었어요.')
  await refreshSession()
  if (
    identity !== useSessionStore.getState().identity ||
    roomFor(workId)?.id !== room.id
  )
    throw new Error('대화방이 바뀌었어요.')
  const session = useSessionStore.getState()
  session.applyRoom(created)
  session.select(workId, created.id)
  detachRoomOperation(workId)
  useChatStore.getState().reset(workId)
  useChatStore
    .getState()
    .patch(workId, { roomId: created.id, opened: true, fresh: false })
  go(`/chat/${created.id}`)
  return created
}

/** 명령어 실행 — 비용 안내·속마음 미리보기는 호출하는 쪽(UI)이 먼저 거친다 */
export function sendCommand(workId: string, key: string, cost: number) {
  const room = roomFor(workId)
  const chat = useChatStore.getState().get(workId)
  if (!room || chat.busy || !chat.opened) return
  if (unresolved(room.id)) return
  const id = requestId()
  const request: PendingRequest = {
    roomId: room.id,
    path: 'command',
    body: { requestId: id, command: key },
    operationId: null,
    status: 'submitting',
    ...requestContext(room),
  }
  if (key !== '!속마음')
    useChatStore
      .getState()
      .patch(workId, { local: [{ t: 'user', key: `pending-${id}`, v: key }] })
  void transmit(workId, request, cost)
}

/**
 * 서버 방을 다시 받아 붙인다 (보내던 요청 복구·새로고침).
 * 서버가 알려 준 작업 ID에 조회로 재연결한다. 새로고침 복구에서는 POST를 보내지 않는다.
 */
export async function reloadRoom(catalog: Catalog, workId: string) {
  const room = roomFor(workId)
  if (!room) return
  const identity = useSessionStore.getState().identity
  const latest = await api<Room>(`/room/${room.id}/`)
  if (
    identity !== useSessionStore.getState().identity ||
    roomFor(workId)?.id !== room.id
  )
    return
  useSessionStore.getState().applyRoom(latest)
  const pending = readPendingRequest(
    readLocal<unknown>(userId(), `pending:${room.id}`, null),
    room.id
  )
  const serverOp = serverPending(latest.pendingOperation)
  if (serverOp?.id && !useChatStore.getState().get(workId).busy) {
    const request: PendingRequest | null =
      pending &&
      (pending.operationId === serverOp.id ||
        pending.body.requestId === serverOp.requestId)
        ? { ...pending, operationId: serverOp.id }
        : null
    if (!request) {
      await resumeUntracked(workId, room.id, serverOp.id, identity)
      return
    }
    const cost = requestCost(catalog, latest, request)
    if (request.body.text)
      useChatStore.getState().patch(workId, {
        local: [
          {
            t: 'user',
            key: `pending-${request.body.requestId}`,
            v: request.body.text,
            channel: request.body.inputType || 'dialogue',
          },
        ],
      })
    await transmit(workId, request, cost, true)
    return
  }
  if (pending && hasRequestMessage(latest, pending.body.requestId)) {
    writeLocal(userId(), `pending:${room.id}`, null)
    useChatStore
      .getState()
      .patch(workId, { requestSnapshot: null, requestError: null })
  } else if (pending) {
    if (pending.operationId && !useChatStore.getState().get(workId).busy) {
      await transmit(
        workId,
        pending,
        requestCost(catalog, latest, pending),
        true
      )
      return
    }
    const snapshot: PendingRequest =
      pending.status === 'failed' ? pending : { ...pending, status: 'unknown' }
    writeLocal(userId(), `pending:${room.id}`, snapshot)
    useChatStore.getState().patch(workId, {
      requestSnapshot: snapshot,
      requestError: {
        message:
          snapshot.rejectionCode === 'INSUFFICIENT_FUNDS'
            ? '젬이 부족해요. 충전 후 다시 보내 주세요.'
            : '보내던 요청의 상태를 확인할 수 없어요.',
        retryable: snapshot.status === 'failed' && snapshot.retryable === true,
        ...(snapshot.rejectionCode ? { code: snapshot.rejectionCode } : {}),
      },
    })
    const draftKey =
      pending.body.inputType === 'ooc'
        ? `draft:ooc:${room.id}`
        : `draft:${room.id}`
    if (
      pending.body.text &&
      !readLocal<string | null>(userId(), draftKey, null)
    )
      useChatStore.getState().patch(workId, {
        restoreDraft: pending.body.text,
        restoreDraftChannel: pending.body.inputType || 'dialogue',
      })
  }
}

/** 명시적 재시도만 실패한 동일 스냅샷을 다시 제출한다. */
export async function retryPending(
  catalog: Catalog,
  workId: string
): Promise<boolean> {
  const room = roomFor(workId)
  const chat = useChatStore.getState().get(workId)
  if (!room || chat.busy) return false
  const pending = readPendingRequest(
    readLocal<unknown>(userId(), `pending:${room.id}`, null),
    room.id
  )
  if (!pending || pending.canReissue === false || pending.status !== 'failed') {
    toast.show(
      '다시 보낼 수 있는 요청이 없어요. 입력창의 메시지를 확인해 주세요.'
    )
    return false
  }
  const rejected =
    !pending.operationId &&
    (!!pending.rejectionCode ||
      chat.requestError?.code === 'INSUFFICIENT_FUNDS')
  if (
    (!rejected && (!pending.operationId || pending.retryable !== true)) ||
    contextChanged(pending, room)
  ) {
    toast.show(
      '대화 설정이나 요청 상태가 바뀌었어요. 입력창에서 확인 후 보내 주세요.'
    )
    return false
  }
  const identity = useSessionStore.getState().identity
  const controller = attach(workId)
  const current = () => active(workId, room.id, identity, controller)
  useChatStore.getState().patch(workId, { busy: true })
  try {
    if (pending.operationId) {
      const operation = await getOperation(
        pending.operationId,
        controller.signal
      )
      if (!current()) return false
      if (
        operation.status !== 'failed' ||
        operation.requestId !== pending.body.requestId ||
        operation.failure?.retryable !== true
      ) {
        toast.show('이 요청은 다시 보내기 전에 처리 상태를 확인해야 해요.')
        return false
      }
    } else {
      const latest = await api<Room>(
        `/room/${room.id}/`,
        undefined,
        'GET',
        controller.signal
      )
      if (!current()) return false
      useSessionStore.getState().applyRoom(latest)
      if (hasRequestMessage(latest, pending.body.requestId)) {
        writeLocal(userId(), `pending:${room.id}`, null)
        useChatStore.getState().patch(workId, {
          requestSnapshot: null,
          requestError: null,
          local: [],
        })
        return true
      }
      if (latest.pendingOperation) {
        const unknown: PendingRequest = {
          ...pending,
          status: 'unknown',
          retryable: false,
        }
        writeLocal(userId(), `pending:${room.id}`, unknown)
        useChatStore.getState().patch(workId, {
          requestSnapshot: unknown,
          requestError: {
            message: '진행 중인 답변이 있어요. 요청 상태를 먼저 확인해 주세요.',
            retryable: false,
          },
        })
        return false
      }
    }
    const latest = roomFor(workId)
    if (!current() || !latest) return false
    if (contextChanged(pending, latest)) {
      toast.show('대화 설정이 바뀌었어요. 입력창에서 확인 후 보내 주세요.')
      return false
    }
    const request: PendingRequest = {
      ...pending,
      operationId: null,
      status: 'submitting',
      retryable: undefined,
      rejectionCode: undefined,
    }
    const cost = requestCost(catalog, latest, request)
    if (cost === null && !pending.operationId) {
      toast.show('요금을 확인할 수 없어 요청을 다시 보낼 수 없어요.')
      return false
    }
    if (request.body.text)
      useChatStore.getState().patch(workId, {
        local: [
          {
            t: 'user',
            key: `pending-${request.body.requestId}`,
            v: request.body.text,
            channel: request.body.inputType || 'dialogue',
          },
        ],
      })
    await transmit(workId, request, cost)
    return true
  } catch (error) {
    if (current())
      toast.show(
        error instanceof Error
          ? error.message
          : '요청 상태를 확인하지 못했어요. 다시 시도해 주세요.'
      )
    return false
  } finally {
    if (attached.get(workId) === controller) {
      attached.delete(workId)
      if (current()) useChatStore.getState().patch(workId, { busy: false })
    }
  }
}

/** 전송 결과를 모를 때 방과 작업을 먼저 조회한다. 사용자가 선택한 경우에만 동일 본문을 재제출한다. */
export async function recoverPending(
  catalog: Catalog,
  workId: string
): Promise<boolean> {
  const room = roomFor(workId)
  if (!room || useChatStore.getState().get(workId).busy) return false
  const pending = readPendingRequest(
    readLocal<unknown>(userId(), `pending:${room.id}`, null),
    room.id
  )
  if (!pending || pending.status !== 'unknown') return false
  const identity = useSessionStore.getState().identity
  useChatStore.getState().patch(workId, { busy: true })
  try {
    const latest = await api<Room>(`/room/${room.id}/`)
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return false
    useSessionStore.getState().applyRoom(latest)
    if (hasRequestMessage(latest, pending.body.requestId)) {
      writeLocal(userId(), `pending:${room.id}`, null)
      useChatStore
        .getState()
        .patch(workId, { requestSnapshot: null, requestError: null, local: [] })
      return true
    }
    const serverOp = serverPending(latest.pendingOperation)
    if (
      serverOp &&
      serverOp.id !== pending.operationId &&
      serverOp.requestId !== pending.body.requestId
    ) {
      const operation = await getOperation(serverOp.id)
      if (
        identity !== useSessionStore.getState().identity ||
        roomFor(workId)?.id !== room.id
      )
        return false
      if (operation.requestId !== pending.body.requestId) {
        useChatStore.getState().patch(workId, {
          requestError: {
            message:
              '이 방에서 다른 생성 작업이 진행 중이에요. 먼저 기존 작업을 확인해 주세요.',
            retryable: false,
          },
        })
        return false
      }
    }
    const operationId = serverOp?.id || pending.operationId
    if (!operationId && pending.canReissue === false) {
      writeLocal(userId(), `pending:${room.id}`, null)
      useChatStore.getState().patch(workId, {
        requestSnapshot: null,
        requestError: {
          message:
            '이전 버전의 요청 형식을 확인하지 못했어요. 원문은 초안에 보관했으니 확인 후 새 요청으로 보내 주세요.',
          retryable: false,
        },
      })
      return false
    }
    if (!operationId && contextChanged(pending, latest)) {
      writeLocal(userId(), `pending:${room.id}`, null)
      useChatStore
        .getState()
        .patch(workId, { requestSnapshot: null, requestError: null })
      return false
    }
    const request = operationId ? { ...pending, operationId } : pending
    const cost = requestCost(catalog, latest, pending)
    if (!operationId && cost === null) {
      toast.show('요금을 확인할 수 없어 요청을 다시 보낼 수 없어요.')
      return false
    }
    await transmit(workId, request, cost, !!operationId)
    return true
  } catch (error) {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      toast.show(
        error instanceof Error
          ? error.message
          : '요청 상태를 확인하지 못했어요.',
        { duration: 3200 }
      )
    return false
  } finally {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id &&
      !attached.has(workId)
    )
      useChatStore.getState().patch(workId, { busy: false })
  }
}

export async function cancelPending(workId: string) {
  const room = roomFor(workId)
  const pending = useChatStore.getState().get(workId).pendingOperation
  if (!room || !pending?.id || cancelling.has(pending.id)) return
  cancelling.add(pending.id)
  const identity = useSessionStore.getState().identity
  try {
    try {
      await api(`/operation/${pending.id}/cancel/`, {})
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 409)) throw error
    }
    const terminal = await getOperation(pending.id)
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    if (terminal.status === 'queued' || terminal.status === 'running') return
    if (terminal.status === 'complete') await refreshSession()
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    detachRoomOperation(workId)
    writeLocal(userId(), `pending:${room.id}`, null)
    const latest = await api<Room>(`/room/${room.id}/`)
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    useSessionStore.getState().applyRoom(latest)
    useChatStore.getState().patch(workId, {
      requestSnapshot: null,
      requestError: null,
      local: [],
      sugsUsed: false,
      lastOperation: terminal,
    })
  } catch (error) {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      toast.show(
        error instanceof Error ? error.message : '취소하지 못했어요.',
        { duration: 3200 }
      )
  } finally {
    cancelling.delete(pending.id)
  }
}

/** 장면 이동 — 장소를 옮기면 30분이 흐른다 */
export async function moveScene(workId: string, place: string) {
  const room = roomFor(workId)
  if (!room || useChatStore.getState().get(workId).busy) return
  const identity = useSessionStore.getState().identity
  useChatStore.getState().patch(workId, { busy: true })
  try {
    const result = await runOperation<RoomResult>(`/room/${room.id}/scene/`, {
      place,
      requestId: requestId(),
    })
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return
    accept(result)
  } catch (error) {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      toast.show(
        error instanceof Error ? error.message : '연결을 확인해 주세요.',
        { duration: 3200 }
      )
  } finally {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      useChatStore.getState().patch(workId, { busy: false })
  }
}

/** 리플레이 — 내 마지막 답장부터 되돌린다. 쓴 재화는 돌려주지 않는다 (v2 doReplay) */
export async function rewindLast(workId: string): Promise<string | null> {
  const room = roomFor(workId)
  const message = room?.messages
    .filter((m) => m.role === 'user' && m.turnId)
    .at(-1)
  if (!room || !message?.turnId) {
    toast.show('되돌릴 답장이 없어요')
    return null
  }
  const identity = useSessionStore.getState().identity
  try {
    const result = await api<RoomResult>(`/room/${room.id}/rewind/`, {
      turnId: message.turnId,
    })
    if (
      identity !== useSessionStore.getState().identity ||
      roomFor(workId)?.id !== room.id
    )
      return null
    accept(result)
    writeLocal(userId(), `draft:${room.id}`, message.content)
    useChatStore.getState().patch(workId, { local: [], sugsUsed: false })
    toast.show('답장을 되돌렸어요. 사용한 재화는 유지됩니다.')
    return message.content
  } catch (error) {
    if (
      identity === useSessionStore.getState().identity &&
      roomFor(workId)?.id === room.id
    )
      toast.show(
        error instanceof Error ? error.message : '연결을 확인해 주세요.',
        { duration: 3200 }
      )
    return null
  }
}

/** 유료 부가 요청 — 스냅 / 다른 답장 (v2-paid-extras request) */
export async function paidRequest(
  catalog: Catalog,
  workId: string,
  kind: 'snaps' | 'suggestions'
) {
  const room = roomFor(workId)
  const chat = useChatStore.getState().get(workId)
  if (!room) return
  if (chat.busy) {
    toast.show(
      kind === 'snaps'
        ? '지금 답장을 만드는 중이에요. 끝나면 다시 눌러 주세요.'
        : '지금 처리하는 중이에요. 잠시만 기다려 주세요.',
      { duration: 2500 }
    )
    return
  }
  const key = `pending:${room.id}`
  const stored = readPendingRequest(
    readLocal<unknown>(userId(), key, null),
    room.id
  )
  const previous =
    chat.requestSnapshot?.roomId === room.id ? chat.requestSnapshot : stored
  if (
    unresolved(room.id) ||
    previous?.status === 'unknown' ||
    (previous?.status === 'failed' &&
      (previous.retryable || previous.rejectionCode))
  )
    return
  if (previous?.status === 'failed') {
    writeLocal(userId(), key, null)
    useChatStore
      .getState()
      .patch(workId, { requestSnapshot: null, requestError: null })
  }
  const identity = useSessionStore.getState().identity
  const request: PendingRequest = {
    roomId: room.id,
    path: kind === 'snaps' ? 'snap' : 'suggestion',
    body: { requestId: requestId() },
    operationId: null,
    status: 'submitting',
    ...requestContext(room),
  }
  const cost = requestCost(catalog, room, request)
  if (cost === null) {
    toast.show('요금을 확인할 수 없어 요청을 보내지 않았어요.')
    return
  }
  if (
    identity !== useSessionStore.getState().identity ||
    roomFor(workId)?.id !== room.id
  )
    return
  await transmit(workId, request, cost)
}

/* ---------- 기억 ---------- */
/** path 는 '' (새 기억) 또는 '/{기억 id}' */
export async function memoryRequest(
  roomId: string,
  path: string,
  body: unknown,
  method: 'POST' | 'PATCH' | 'DELETE'
) {
  const room = await api<Room>(
    `/room/${roomId}/memory${path}/`,
    method === 'DELETE' ? undefined : body,
    method
  )
  useSessionStore.getState().applyRoom(room)
  return room
}
